import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { UNKNOWN_VALUE_LABEL } from "@/config/intelligenceFlags";

type ManifestRow = {
  id: string;
  assessment_id: string;
  dna_version: number;
  is_current: boolean;
  status: string;
  record_kind: string;
  portfolio_id: string | null;
  portfolio_name: string | null;
  company_id: string | null;
  questionnaire_id: string | null;
  question_count: number | null;
  content_hash: string | null;
  generated_at: string | null;
  frozen_at: string | null;
};

type AssessmentRow = {
  id: string;
  org_name: string | null;
  questionnaire_id: string | null;
  status: string | null;
};

const unknown = (v: unknown) =>
  v === null || v === undefined || v === "" ? (
    <span className="text-muted-foreground italic">{UNKNOWN_VALUE_LABEL}</span>
  ) : (
    <>{String(v)}</>
  );

const kindVariant = (kind: string) =>
  kind === "native" ? ("default" as const) : kind === "reconstructed" ? ("secondary" as const) : ("outline" as const);

const kindLabel = (kind: string) =>
  kind === "native" ? "Native" : kind === "reconstructed" ? "Reconstructed" : "Unknown";

export default function AssessmentManifests() {
  const { toast } = useToast();
  const [rows, setRows] = useState<ManifestRow[]>([]);
  const [assessments, setAssessments] = useState<AssessmentRow[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);

  const load = async () => {
    setLoading(true);
    const [{ data: manifests, error: mErr }, { data: list }] = await Promise.all([
      (supabase.rpc as any)("admin_list_assessment_dna", { p_limit: 200, p_offset: 0 }),
      (supabase.rpc as any)("admin_list_assessments"),
    ]);
    if (mErr) {
      toast({ title: "Unable to load manifests", description: mErr.message, variant: "destructive" });
    }
    setRows((manifests as ManifestRow[]) ?? []);
    setAssessments((list as AssessmentRow[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const generate = async () => {
    if (!selected) return;
    setWorking(true);
    const { error } = await (supabase.rpc as any)("generate_assessment_dna", {
      p_assessment_id: selected,
      p_reason: "Admin manual draft generation",
    });
    setWorking(false);
    if (error) {
      toast({ title: "Generation failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Draft manifest generated", description: "No assessment data was modified." });
    void load();
  };

  const viewDetail = async (assessmentId: string, version: number) => {
    const { data, error } = await (supabase.rpc as any)("get_assessment_dna", {
      p_assessment_id: assessmentId,
      p_dna_version: version,
    });
    if (error) {
      toast({ title: "Unable to load manifest", description: error.message, variant: "destructive" });
      return;
    }
    setDetail((data as Record<string, unknown>[])?.[0] ?? null);
  };

  const assessmentLabel = useMemo(() => {
    const map = new Map(assessments.map((a) => [a.id, a]));
    return (id: string) => {
      const a = map.get(id);
      return a ? `${a.org_name ?? "—"} · ${a.questionnaire_id ?? "—"}` : id;
    };
  }, [assessments]);

  return (
    <AppLayout
      title="Assessment Manifests | Admin"
      description="Read-only viewer for assessment composition and methodology manifests."
    >
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Assessment Manifests</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Read-only record of what each assessment was composed of and which methodology
          produced it. Generating a draft manifest does not modify any assessment, score,
          finding or report.
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Generate Draft Manifest</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-[420px]">
              <SelectValue placeholder="Select an assessment…" />
            </SelectTrigger>
            <SelectContent>
              {assessments.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.org_name ?? "—"} · {a.questionnaire_id ?? "—"} · {a.status ?? "—"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={generate} disabled={!selected || working}>
            {working ? "Generating…" : "Generate Draft Manifest"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Manifests {loading ? "" : `(${rows.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No manifests exist. None are created automatically.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assessment</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Record kind</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Questionnaire</TableHead>
                  <TableHead>Questions</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Content hash</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="max-w-[280px] truncate">
                      {r.portfolio_name ?? assessmentLabel(r.assessment_id)}
                    </TableCell>
                    <TableCell>
                      v{r.dna_version}
                      {r.is_current && (
                        <Badge variant="outline" className="ml-2">
                          Current
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={kindVariant(r.record_kind)}>{kindLabel(r.record_kind)}</Badge>
                    </TableCell>
                    <TableCell className="capitalize">{r.status}</TableCell>
                    <TableCell>{unknown(r.questionnaire_id)}</TableCell>
                    <TableCell>{unknown(r.question_count)}</TableCell>
                    <TableCell>{unknown(r.company_id)}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.content_hash ? r.content_hash.slice(0, 12) : unknown(null)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewDetail(r.assessment_id, r.dna_version)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {detail && (
        <Card className="mt-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Manifest detail</CardTitle>
            <Button size="sm" variant="ghost" onClick={() => setDetail(null)}>
              Close
            </Button>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-2 md:grid-cols-2">
              {Object.entries(detail).map(([k, v]) => (
                <div key={k} className="flex gap-2 border-b py-1 text-sm">
                  <dt className="w-56 shrink-0 text-muted-foreground">{k}</dt>
                  <dd className="min-w-0 break-words">
                    {v === null || v === undefined || v === "" ? (
                      <span className="italic text-muted-foreground">{UNKNOWN_VALUE_LABEL}</span>
                    ) : typeof v === "object" ? (
                      <code className="text-xs">{JSON.stringify(v)}</code>
                    ) : (
                      String(v)
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      )}
    </AppLayout>
  );
}
